import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { schedulesApi } from '@/lib/api';

// ─── Generic Recurring Editor ────────────────────────────────────────────────
// Lets the operator manage a list of recurring weekly schedule slots that get
// expanded into concrete StreamSchedule rows on each sync (next 30 days).
//
// Shared by the admin Facility Settings page and the club-manager Schedules page.
// The underlying /generic-entries + /sync-from-generic endpoints derive the
// facility from the caller's token for facility (club-manager) accounts, so this
// component is safe to expose to club managers for their own facility.
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

export default function GenericRecurringEditor({ facilityId, sheetNames }: { facilityId: string; sheetNames?: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['generic-entries', facilityId],
    queryFn: () => schedulesApi.listGenericEntries(facilityId),
  });
  const entries = data?.entries ?? [];

  const facilitySheets: string[] = React.useMemo(() => {
    try {
      const v = JSON.parse(sheetNames || '[]');
      return Array.isArray(v) && v.length > 0 ? v.map(String) : ['A','B','C','D'];
    } catch { return ['A','B','C','D']; }
  }, [sheetNames]);

  const [draft, setDraft] = useState({
    dayOfWeek: 1,       // Mon by default
    startTime: '19:00',
    endTime:   '21:00',
    leagueName: '',
    sheetIds: [] as string[],  // [] = all
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => schedulesApi.createGenericEntry(facilityId, {
      dayOfWeek: draft.dayOfWeek,
      startTime: draft.startTime,
      endTime:   draft.endTime,
      leagueName: draft.leagueName.trim() || null,
      sheetIds: draft.sheetIds,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['generic-entries', facilityId] });
      setDraft({ dayOfWeek: 1, startTime: '19:00', endTime: '21:00', leagueName: '', sheetIds: [] });
      setError(null);
    },
    onError: (err: any) => setError(err?.message ?? 'Failed to create entry'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => schedulesApi.deleteGenericEntry(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['generic-entries', facilityId] }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      schedulesApi.updateGenericEntry(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['generic-entries', facilityId] }),
  });

  const syncMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromGeneric(facilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-preview', facilityId] });
      // Refresh the concrete schedule list shown on the Schedules page.
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (err: any) => setError(err?.message ?? 'Sync failed'),
  });

  const toggleSheet = (sheet: string) => {
    setDraft(d => ({
      ...d,
      sheetIds: d.sheetIds.includes(sheet)
        ? d.sheetIds.filter(s => s !== sheet)
        : [...d.sheetIds, sheet],
    }));
  };

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Recurring weekly entries</p>

      {/* Existing entries list */}
      {isLoading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No recurring entries yet. Add one below.</p>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th className="px-2 py-1.5 text-left">Day</th>
                <th className="px-2 py-1.5 text-left">Start</th>
                <th className="px-2 py-1.5 text-left">End</th>
                <th className="px-2 py-1.5 text-left">League</th>
                <th className="px-2 py-1.5 text-left">Sheets</th>
                <th className="px-2 py-1.5"></th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {entries.map(e => (
                <tr key={e.id} className={`text-gray-700 dark:text-gray-300 ${!e.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-2 py-1.5 font-medium">{DAY_NAMES[e.dayOfWeek]}</td>
                  <td className="px-2 py-1.5 font-mono">{e.startTime}</td>
                  <td className="px-2 py-1.5 font-mono">{e.endTime}</td>
                  <td className="px-2 py-1.5">{e.leagueName || <span className="text-gray-400">(facility name)</span>}</td>
                  <td className="px-2 py-1.5">{e.sheetIds.length === 0 ? <span className="text-gray-400">all</span> : e.sheetIds.join(', ')}</td>
                  <td className="px-2 py-1.5">
                    <button onClick={() => toggleActiveMutation.mutate({ id: e.id, isActive: !e.isActive })}
                      className="rounded px-2 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
                      {e.isActive ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                  <td className="px-2 py-1.5">
                    <button onClick={() => { if (confirm('Delete this recurring entry?')) deleteMutation.mutate(e.id); }}
                      className="rounded px-2 py-0.5 text-[11px] text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add new entry */}
      <div className="rounded border border-dashed border-gray-300 bg-white p-3 dark:border-gray-700 dark:bg-gray-900/60">
        <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">Add new entry</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
          <div>
            <label className="block text-[11px] text-gray-500 dark:text-gray-400">Day</label>
            <select className={inputCls} value={draft.dayOfWeek}
              onChange={e => setDraft(d => ({ ...d, dayOfWeek: parseInt(e.target.value, 10) }))}>
              {DAY_NAMES.map((n, i) => <option key={i} value={i}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 dark:text-gray-400">Start (24-hr)</label>
            <input className={inputCls} type="time" value={draft.startTime}
              onChange={e => setDraft(d => ({ ...d, startTime: e.target.value }))} />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 dark:text-gray-400">End (24-hr)</label>
            <input className={inputCls} type="time" value={draft.endTime}
              onChange={e => setDraft(d => ({ ...d, endTime: e.target.value }))} />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 dark:text-gray-400">League name (optional)</label>
            <input className={inputCls} type="text" placeholder="defaults to facility name"
              value={draft.leagueName} onChange={e => setDraft(d => ({ ...d, leagueName: e.target.value }))} />
          </div>
        </div>
        <div className="mt-2">
          <label className="block text-[11px] text-gray-500 dark:text-gray-400">
            Sheets {draft.sheetIds.length === 0 && <span className="text-gray-400">(none selected — will apply to all)</span>}
          </label>
          <div className="mt-1 flex flex-wrap gap-1">
            {facilitySheets.map(s => (
              <button key={s} type="button" onClick={() => toggleSheet(s)}
                className={`rounded-full border px-2 py-0.5 text-[11px] ${
                  draft.sheetIds.includes(s)
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
            Add entry
          </Button>
          <Button size="sm" variant="outline" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
            {syncMutation.isPending ? 'Syncing…' : 'Sync now (expand 30 days)'}
          </Button>
          {syncMutation.data && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              imported {syncMutation.data.imported}, replaced {syncMutation.data.deleted}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
